import * as React from 'react';
import {
    BooleanField,
    BooleanInput,
    Create,
    CreateButton,
    Datagrid,
    Edit,
    FunctionField,
    List,
    NumberInput,
    SearchInput,
    SaveButton,
    SelectInput,
    SimpleForm,
    TextField,
    TextInput,
    Toolbar,
    email,
    maxLength,
    maxValue,
    minValue,
    number,
    regex,
    required,
    useNotify,
    useRedirect,
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { PARTNER_TYPES, type Partner } from '../../services/marketing/marketingService.ts';
import PartnerLinkPanel from './PartnerLinkPanel.tsx';
import { errorCode, errorMessage, euro } from './marketingShared.ts';

/** The react-admin resource; the data provider sends it to /admin/partners as is. */
export const PARTNERS_RESOURCE = 'admin/partners';

/** The fields the server accepts on POST and PUT. Everything else on the row is computed. */
const EDITABLE = [
    'code', 'name', 'partnerType', 'ratePercent', 'attributionDays',
    'contactName', 'contactPhone', 'contactEmail', 'notes', 'isActive',
] as const;

const toBody = (data: any) => {
    const body: Record<string, unknown> = {};
    EDITABLE.forEach((key) => { body[key] = data[key] ?? null; });
    body.code = String(data.code ?? '').trim().toUpperCase();
    body.isActive = data.isActive !== false;
    return body;
};

const ListActions = () => {
    const { t } = useTranslation();
    return (
        <Box sx={{ pt: 4, pb: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 'var(--text-xxl)' }}>
                {t('resources.partners')}
            </Typography>
            <CreateButton icon={<AddIcon />} label={t('marketing.partners.add').toLocaleUpperCase()}
                sx={{ color: 'var(--color-primary)', fontSize: '16px', fontWeight: 700 }} />
        </Box>
    );
};

export const PartnersList = () => {
    const { t } = useTranslation();
    const filters = [
        <SearchInput key="q" source="q" alwaysOn placeholder={t('marketing.partners.search')} sx={{ minWidth: 320 }} />,
    ];
    const typeLabel = (type?: string) => (type ? t(`marketing.partnerTypes.${type}`, type) : '—');
    return (
        <List
            actions={<ListActions />}
            filters={filters}
            perPage={25}
            sort={{ field: 'name', order: 'ASC' }}
            title={t('resources.partners')}
            sx={{ '& .RaList-main': { px: 4 } }}
        >
            <Datagrid bulkActionButtons={false} rowClick="edit"
                sx={{ '& .RaDatagrid-headerCell': { fontWeight: 700 }, '& td, & th': { fontSize: 'var(--text-xs)' } }}>
                <TextField source="code" label={t('marketing.partners.code')} />
                <TextField source="name" label={t('marketing.partners.name')} />
                <FunctionField source="partnerType" label={t('marketing.partners.type')}
                    render={(r: Partner) => typeLabel(r?.partnerType)} />
                <FunctionField source="ratePercent" label={t('marketing.partners.rate')} textAlign="right"
                    render={(r: Partner) => `${Number(r?.ratePercent ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 2 })}%`} />
                <BooleanField source="isActive" label={t('marketing.partners.active')} />
                {/* The four figures are all-time and computed by the server. */}
                <FunctionField source="customers" label={t('marketing.partners.customers')} sortable={false} textAlign="right"
                    render={(r: Partner) => r?.customers ?? 0} />
                <FunctionField source="deliveredOrders" label={t('marketing.partners.deliveredOrders')} sortable={false} textAlign="right"
                    render={(r: Partner) => r?.deliveredOrders ?? 0} />
                <FunctionField source="foodSales" label={t('marketing.partners.foodSales')} sortable={false} textAlign="right"
                    render={(r: Partner) => euro(r?.foodSales)} />
                <FunctionField source="commission" label={t('marketing.partners.commission')} sortable={false} textAlign="right"
                    render={(r: Partner) => euro(r?.commission)} />
            </Datagrid>
        </List>
    );
};

const PartnerForm: React.FC<{ creating?: boolean }> = ({ creating }) => {
    const { t } = useTranslation();
    const typeChoices = PARTNER_TYPES.map((id) => ({ id, name: t(`marketing.partnerTypes.${id}`) }));
    return (
        // Save only: partners are never deleted (their customers and commission
        // history must stay); switching "Active" off retires one.
        <SimpleForm sx={{ maxWidth: 720 }} toolbar={<Toolbar><SaveButton /></Toolbar>}>
            <TextInput
                source="code"
                label={t('marketing.partners.code')}
                helperText={creating ? t('marketing.partners.codeHelp') : t('marketing.partners.codeChangeHelp')}
                parse={(v: string) => (v ?? '').toUpperCase()}
                validate={[
                    required(),
                    regex(/^[A-Za-z0-9-]{2,32}$/, t('marketing.partners.codeInvalid')),
                ]}
                fullWidth
            />
            <TextInput source="name" label={t('marketing.partners.name')} validate={[required(), maxLength(200)]} fullWidth />
            <SelectInput source="partnerType" label={t('marketing.partners.type')} choices={typeChoices} validate={required()} />
            <NumberInput
                source="ratePercent"
                label={t('marketing.partners.ratePercent')}
                helperText={t('marketing.partners.rateHelp')}
                validate={[required(), number(), minValue(0), maxValue(100)]}
                min={0}
                max={100}
                step={0.5}
            />
            <NumberInput
                source="attributionDays"
                label={t('marketing.partners.attributionDays')}
                helperText={t('marketing.partners.attributionDaysHelp')}
                validate={[
                    required(), number(), minValue(1), maxValue(3650),
                    (v: any) => (v != null && !Number.isInteger(Number(v)) ? t('marketing.partners.wholeDays') : undefined),
                ]}
                min={1}
                max={3650}
                step={1}
            />
            <TextInput source="contactName" label={t('marketing.partners.contactName')} validate={maxLength(200)} fullWidth />
            <TextInput source="contactPhone" label={t('marketing.partners.contactPhone')} validate={maxLength(64)} fullWidth />
            <TextInput source="contactEmail" label={t('marketing.partners.contactEmail')} validate={[email(), maxLength(200)]} fullWidth />
            <TextInput source="notes" label={t('marketing.partners.notes')} multiline minRows={3} fullWidth />
            <BooleanInput source="isActive" label={t('marketing.partners.active')} helperText={t('marketing.partners.activeHelp')} />
        </SimpleForm>
    );
};

/** A 409 on a taken code gets its own words; anything else shows what the server said. */
const useSaveError = () => {
    const notify = useNotify();
    const { t } = useTranslation();
    return (e: any) => {
        if (errorCode(e) === 'error.partner_code_taken') {
            notify(t('marketing.partners.codeTaken'), { type: 'error' });
        } else {
            notify(errorMessage(e, e?.message || t('common.saveFailed')), { type: 'error' });
        }
    };
};

export const PartnerCreate = () => {
    const { t } = useTranslation();
    const onError = useSaveError();
    const notify = useNotify();
    const redirect = useRedirect();
    return (
        <Create
            title={t('marketing.partners.add')}
            transform={toBody}
            mutationOptions={{
                onError,
                // Straight to the new partner's page: that is where its link and QR code are.
                onSuccess: (data: any) => {
                    notify(t('common.saved'), { type: 'success' });
                    redirect('edit', PARTNERS_RESOURCE, data.id);
                },
            }}
            record={{ ratePercent: 0, attributionDays: 365, isActive: true }}
        >
            <PartnerForm creating />
        </Create>
    );
};

export const PartnerEdit = () => {
    const { t } = useTranslation();
    const onError = useSaveError();
    return (
        // Pessimistic: a taken code is only found out by the server, and an
        // undoable save would have shown "saved" before it said no.
        <Edit title={t('resources.partners')} transform={toBody} mutationMode="pessimistic" mutationOptions={{ onError }}>
            <PartnerForm />
            <PartnerLinkPanel />
        </Edit>
    );
};
